import { useEffect, useId, useRef, useState } from 'react'
import { api, imageUrl } from '../api'
import { useI18n } from '../i18n'
import type { CatalogItem } from '../protocol'

const DEBOUNCE_MS = 250

interface Props {
  token?: string
  onPick: (item: CatalogItem) => void
  autoFocus?: boolean
}

/** Title search with suggestions as you type. */
export function CatalogSearch({ token, onPick, autoFocus }: Props) {
  const { t, lang } = useI18n()
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<CatalogItem[]>([])
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [error, setError] = useState('')
  const [active, setActive] = useState(-1)
  const [open, setOpen] = useState(false)
  const listId = useId()
  const wrap = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setItems([])
      setState('idle')
      return
    }
    const ctrl = new AbortController()
    const timer = window.setTimeout(async () => {
      setState('loading')
      try {
        const res = await api.catalogSearch(q, lang, token, ctrl.signal)
        setItems(res.items)
        setActive(res.items.length ? 0 : -1)
        setState('done')
      } catch (e) {
        if (ctrl.signal.aborted) return
        setError(e instanceof Error ? e.message : String(e))
        setState('error')
      }
    }, DEBOUNCE_MS)
    return () => {
      ctrl.abort()
      window.clearTimeout(timer)
    }
  }, [query, lang, token])

  useEffect(() => {
    const close = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [])

  const pick = (item: CatalogItem) => {
    setOpen(false)
    onPick(item)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((a) => Math.min(items.length - 1, a + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(0, a - 1))
    } else if (e.key === 'Enter' && items[active]) {
      e.preventDefault()
      pick(items[active])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const showList = open && query.trim().length >= 2
  return (
    <div className="csearch" ref={wrap}>
      <input
        className="csearch__input"
        type="search"
        value={query}
        autoFocus={autoFocus}
        placeholder={t('catalog.searchPlaceholder')}
        aria-label={t('catalog.searchLabel')}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <ul className="csearch__list" id={listId} role="listbox">
          {state === 'loading' && items.length === 0 && <li className="csearch__note">{t('catalog.searching')}</li>}
          {state === 'done' && items.length === 0 && <li className="csearch__note">{t('catalog.noResults')}</li>}
          {state === 'error' && <li className="csearch__note error-text">{error}</li>}
          {items.map((it, i) => (
            <li
              key={`${it.type}${it.id}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              className={`csearch__item${i === active ? ' is-active' : ''}`}
              onPointerEnter={() => setActive(i)}
              onClick={() => pick(it)}
            >
              <Poster path={it.poster} size="w92" className="csearch__poster" />
              <span className="csearch__text">
                <span className="csearch__title">{it.title}</span>
                <span className="csearch__meta muted">
                  {[it.year, t(it.type === 'tv' ? 'catalog.tv' : 'catalog.movie'), it.originalTitle !== it.title ? it.originalTitle : null]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Poster image with a neutral placeholder when TMDb has none. */
export function Poster({ path, size, className = '' }: { path?: string; size: 'w92' | 'w185' | 'w342' | 'w500'; className?: string }) {
  const src = imageUrl(size, path)
  // Per image: another title's poster must neither linger nor inherit a failure.
  const [failed, setFailed] = useState('')
  return src && failed !== src ? (
    <img key={src} className={`poster ${className}`} src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(src)} />
  ) : (
    <span className={`poster poster--empty ${className}`} aria-hidden="true" />
  )
}
